import { afterNextRender, DestroyRef, Directive, ElementRef, inject } from '@angular/core';

const KEYBOARD_NAV_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

/**
 * Sincronizza scroll ed evidenziazione tastiera con aria-activedescendant del combobox.
 *
 * - `data-keyboard-active` solo durante navigazione tastiera (non al passaggio del mouse).
 * - `data-active` di Angular Aria può lampeggiare o restare appiccicato: non lo usiamo per lo sfondo.
 */
@Directive({
  selector: '.aria-listbox',
  standalone: true,
})
export class AriaListboxScrollActiveDirective {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const listbox = this.host.nativeElement;
      const comboboxHost = listbox.closest('.aria-combobox') as HTMLElement | null;
      const input = comboboxHost?.querySelector('[role="combobox"]') as HTMLElement | null;
      if (!input || !comboboxHost) {
        return;
      }

      let keyboardIntent = false;
      let lastActiveId: string | null = null;
      let keyboardLockUntil = 0;
      let syncFrame = 0;

      const isDrawer = (): boolean => listbox.classList.contains('aria-listbox--drawer');

      const clearKeyboardHighlight = (): void => {
        Array.from(listbox.querySelectorAll('[data-keyboard-active]'), (el) => el as HTMLElement)
          .forEach((option) => option.removeAttribute('data-keyboard-active'));
      };

      const resetKeyboardState = (): void => {
        keyboardIntent = false;
        lastActiveId = null;
        clearKeyboardHighlight();
      };

      const sync = (): void => {
        let activeId = input.getAttribute('aria-activedescendant');
        if (activeId) {
          lastActiveId = activeId;
        } else if (keyboardIntent && lastActiveId) {
          activeId = lastActiveId;
        }

        const options = Array.from(
          listbox.querySelectorAll('.aria-listbox__option'),
          (el) => el as HTMLElement,
        );
        const useKeyboardHighlight = keyboardIntent && !isDrawer();
        let activeEl: HTMLElement | null = null;

        for (const option of options) {
          const isActive = Boolean(activeId && option.id === activeId);
          if (isActive) {
            activeEl = option;
            if (useKeyboardHighlight) {
              option.setAttribute('data-keyboard-active', 'true');
            } else {
              option.removeAttribute('data-keyboard-active');
            }
          } else {
            option.removeAttribute('data-keyboard-active');
          }
        }

        activeEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      const scheduleSync = (): void => {
        if (syncFrame) {
          cancelAnimationFrame(syncFrame);
        }
        // Due frame: ngCombobox aggiorna aria-activedescendant dopo il keydown.
        syncFrame = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            syncFrame = 0;
            sync();
          });
        });
      };

      const onInputKeydown = (event: KeyboardEvent): void => {
        if (!KEYBOARD_NAV_KEYS.has(event.key)) {
          return;
        }
        keyboardIntent = true;
        keyboardLockUntil = performance.now() + 200;
        scheduleSync();
      };

      const onPointerMove = (): void => {
        if (!keyboardIntent || performance.now() < keyboardLockUntil) {
          return;
        }
        resetKeyboardState();
      };

      const onExpandedChange = (): void => {
        if (comboboxHost.getAttribute('data-expanded') !== 'true') {
          resetKeyboardState();
        }
      };

      input.addEventListener('keydown', onInputKeydown, { capture: true });
      listbox.addEventListener('pointermove', onPointerMove);

      const inputObserver = new MutationObserver(scheduleSync);
      inputObserver.observe(input, {
        attributes: true,
        attributeFilter: ['aria-activedescendant'],
      });

      const comboboxObserver = new MutationObserver(onExpandedChange);
      comboboxObserver.observe(comboboxHost, {
        attributes: true,
        attributeFilter: ['data-expanded'],
      });

      onExpandedChange();
      scheduleSync();

      this.destroyRef.onDestroy(() => {
        if (syncFrame) {
          cancelAnimationFrame(syncFrame);
        }
        inputObserver.disconnect();
        comboboxObserver.disconnect();
        input.removeEventListener('keydown', onInputKeydown, { capture: true });
        listbox.removeEventListener('pointermove', onPointerMove);
      });
    });
  }
}
